import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";
import { formatLocalDate } from "../../utils/dateTime";

export default function UserPage() {
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null); // edit modal

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
  });

  const fetchData = async () => {
    try {
      const [uRes] = await Promise.all([
        axios.get("/admin/users")
      ]);
      setUsers(uRes.data);
    } catch (err) { console.error("Fetch failed", err); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleStatus = async (user) => {
    const newStatus = user.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
    try {
      await axios.put("/admin/users/status", { id: user.id, status: newStatus });
      fetchData();
    } catch (err) { alert("Status update failed"); }
  };

  const handleUnlock = async (user) => {
    if (!window.confirm(`Are you sure you want to unlock ${user.name}'s account?`)) return;

    try {
      await axios.put("/admin/users/unlock", { id: user.id });
      fetchData();
      alert("User account unlocked successfully!");
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to unlock user");
    }
  };

  const handleModify = async (e) => {
    e.preventDefault();
    try {
      await axios.put("/admin/users/modify", editUser);
      setEditUser(null);
      fetchData();
      alert("User updated successfully!");
    } catch (err) { alert("Update failed"); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/admin/users", {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
      });
      setShowCreate(false);
      setNewUser({ name: "", email: "", password: "" });
      fetchData();
      alert("Client created successfully!");
    } catch (err) {
      alert(err?.response?.data?.error || "User creation failed");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl md:text-3xl font-black text-gray-800">Client Control Center</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-5 py-2 rounded-xl font-black shadow-lg hover:bg-blue-700 transition"
          >
            + Create Client
          </button>
        </div>

        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-x-auto">
          <table className="min-w-[900px] w-full text-left">
            <thead className="bg-gray-900 text-white text-[10px] uppercase font-bold tracking-widest">
              <tr>
                <th className="p-5">Client Profile</th>
                <th className="p-5">Subscription Status</th>
                <th className="p-5 text-center">Account Access</th>
                <th className="p-5">Management</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => {
                const diffTime = new Date(u.plan_expiry_date) - new Date();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                const active = u.status === 'ACTIVE';
                const isExpired = diffDays <= 0;

                return (
                  <tr key={u.id} className={!active ? 'bg-red-50/40' : 'hover:bg-blue-50/20 transition'}>
                    <td className="p-5">
                      <p className="font-bold text-gray-800">{u.name || "N/A"}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="p-5">
                      <div className="flex flex-col">
                        <span className={`text-xs font-black ${isExpired ? 'text-red-500' : 'text-blue-600'}`}>
                          {u.plan_expiry_date ? `${isExpired ? 0 : diffDays} Days Remaining` : "No Active Plan"}
                        </span>
                        <span className="text-[10px] text-gray-400">Expires: {u.plan_expiry_date ? formatLocalDate(u.plan_expiry_date) : 'N/A'}</span>
                      </div>
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() => handleStatus(u)}
                          className={`px-4 py-1.5 rounded-xl text-[10px] font-black transition-all ${active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200'}`}
                        >
                          {active ? "● ACTIVE" : "● DISABLED"}
                        </button>

                        {(u.failed_login_attempts >= 5 || (u.locked_until && new Date(u.locked_until) > new Date())) && (
                          <span className="text-[10px] font-black bg-orange-100 text-orange-700 px-3 py-1 rounded-lg flex items-center gap-1 border border-orange-200">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                            LOCKED
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-5 flex items-center justify-center gap-4 h-full align-middle">
                      <button
                        onClick={() => setEditUser(u)}
                        className="text-gray-400 font-bold hover:text-blue-600 transition-colors flex items-center gap-1"
                      >
                        MODIFY <span className="text-lg leading-none">✎</span>
                      </button>

                      {(u.failed_login_attempts >= 5 || (u.locked_until && new Date(u.locked_until) > new Date())) && (
                        <button
                          onClick={() => handleUnlock(u)}
                          className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-[10px] font-black hover:bg-gray-800 transition-colors flex items-center gap-1 border border-gray-700 shadow-sm"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                          UNLOCK
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Create Client Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-[2.5rem] w-full max-w-md shadow-2xl border border-white">
              <h3 className="text-2xl font-black mb-2 text-gray-800">Create Client</h3>
              <p className="text-gray-400 text-sm mb-6">Create a new client account and optionally assign a plan.</p>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase ml-2 text-gray-400">Full Name</label>
                  <input
                    className="w-full border border-gray-100 bg-gray-50 p-4 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Client name"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase ml-2 text-gray-400">Email Address</label>
                  <input
                    type="email"
                    className="w-full border border-gray-100 bg-gray-50 p-4 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="client@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase ml-2 text-gray-400">Password</label>
                  <input
                    type="password"
                    className="w-full border border-gray-100 bg-gray-50 p-4 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Set a password"
                    required
                  />
                </div>

                {/* Plan selection removed as requested */}

                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreate(false)}
                    className="flex-1 bg-gray-100 py-4 rounded-2xl font-black text-gray-500 hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-blue-200 hover:bg-blue-700 transition"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modify User Modal */}
        {editUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-[2.5rem] w-full max-w-md shadow-2xl border border-white">
              <h3 className="text-2xl font-black mb-2 text-gray-800">Edit Client</h3>
              <p className="text-gray-400 text-sm mb-6">Update profile details for this user.</p>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase ml-2 text-gray-400">Full Name</label>
                  <input className="w-full border border-gray-100 bg-gray-50 p-4 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} placeholder="Enter name" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase ml-2 text-gray-400">Email Address</label>
                  <input className="w-full border border-gray-100 bg-gray-50 p-4 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} placeholder="Enter email" />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={() => setEditUser(null)} className="flex-1 bg-gray-100 py-4 rounded-2xl font-black text-gray-500 hover:bg-gray-200 transition">Cancel</button>
                <button onClick={handleModify} className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-blue-200 hover:bg-blue-700 transition">Update User</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}